import { useQueries, useQuery } from "@tanstack/react-query";
import { Link } from "@tanstack/react-router";
import type { CaseView } from "shared";
import { api } from "../api";
import { STATUS_TEXT, worksheetsKey, type WorksheetRow } from "../components/WorksheetPanel";

const MAX_CASES = 30;

/**
 * Worksheets across my open cases. There is no cross-case list endpoint yet, so this reads the per-case list for the
 * first 30 open cases; a server list endpoint would replace this. Only case titles are shown, never customer details.
 */
export function Worksheets() {
  const cases = useQuery({ queryKey: ["cases", "open", ""], queryFn: () => api<{ cases: CaseView[] }>("/api/cases?scope=open") });
  const list = (cases.data?.cases ?? []).slice(0, MAX_CASES);
  const results = useQueries({
    queries: list.map((c) => ({ queryKey: worksheetsKey(c.id), queryFn: () => api<{ worksheets: WorksheetRow[] }>(`/api/cases/${c.id}/worksheets`) })),
  });
  const rows = list.flatMap((c, i) => (results[i]?.data?.worksheets ?? []).map((w) => ({ c, w })));
  const loading = cases.isLoading || results.some((r) => r.isLoading);
  return (
    <div className="mx-auto max-w-3xl">
      <h1 className="mb-4 text-xl font-semibold">Worksheets</h1>
      {loading && <p className="text-sm text-slate-500">Loading…</p>}
      {cases.error && <p role="alert" className="text-sm text-red-600">Could not load your cases.</p>}
      {results.some((r) => r.isError) && <p role="alert" className="text-sm text-red-600">Some worksheets could not be loaded.</p>}
      {!loading && !cases.error && rows.length === 0 && <p className="text-sm text-slate-500">No worksheets on your open cases.</p>}
      <ul className="divide-y divide-slate-200 rounded-lg border border-slate-200 bg-white dark:divide-slate-800 dark:border-slate-800 dark:bg-slate-900">
        {rows.map(({ c, w }) => (
          <li key={w.id} className="flex flex-wrap items-center gap-3 px-4 py-3 text-sm">
            <Link to="/cases/$id" params={{ id: c.id }} className="font-medium text-brand-700 hover:underline dark:text-brand-100">{c.title}</Link>
            <span>{w.programName ?? "Worksheet"}</span>
            <span className="text-slate-600 dark:text-slate-400">{STATUS_TEXT[w.status]}</span>
            {w.status !== "sent" && w.missing > 0 && <span className="text-xs text-slate-500">{w.missing} required answer{w.missing === 1 ? "" : "s"} missing</span>}
            <span className="ml-auto text-xs text-slate-500">{new Date(w.updatedAt).toLocaleDateString()}</span>
          </li>
        ))}
      </ul>
      {list.length === MAX_CASES && <p className="mt-2 text-xs text-slate-500">Showing worksheets from your first {MAX_CASES} open cases.</p>}
    </div>
  );
}
